/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview B2B Events logic module.
 * Handles B2B event CRUD and participating companies via generic event API.
 * @module logic/b2bEvents
 */

import {
  HTTP_BAD_REQUEST,
  HTTP_NOT_FOUND,
  fetchUserRoleData,
  logDevError,
  getAuthJsonHeaders,
  normalizeAudienciaFromApi,
  resolveExpiredToken,
  showEventCreatedSuccess,
  uploadEventBannerFromDataUrl,
} from "../../utils/eventApi.js";
import {
  TIME_COLON_INDEX,
  TIME_INPUT_HH_MM_LENGTH,
  filterBusinessStyleEventsForUserList,
  isActiveB2BEventVisible,
  isB2BManagementEventVisible,
} from "../../utils/eventDateFilters.js";
import {
  canViewPastEvents,
  filterNonCancelledEvents,
  sortEventsFuturesFirst,
} from "../../utils/pastEventsAccess.js";
import { getUser } from "../user";
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
const B2B_EVENT_NOT_FOUND_MESSAGE = "B2B Event not found";
const DATE_FORMAT_YYYY_MM_DD = "YYYY-MM-DD";

export { showEventCreatedSuccess };

/** `input type="time"` expects HH:mm; API often returns HH:mm:ss. */
const normalizeTimeForInput = (value) => {
  if (value == null || value === "") {
    return null;
  }
  const s = String(value).trim();
  if (s.length >= TIME_INPUT_HH_MM_LENGTH && s[TIME_COLON_INDEX] === ":") {
    return s.slice(0, TIME_INPUT_HH_MM_LENGTH);
  }
  return s || null;
};

export { normalizeAudienciaFromApi };

export const getUserRoleData = fetchUserRoleData;

/**
 * List B2B events visible to the user (lightweight).
 * Backend applies filtering (activo, fechaFinEvento, fechaDisponibleSocios/NoSocios, user type).
 * Does not include userAppointments, disponibilidadEjecutivosRegistro, or duration hints — use {@link getB2BEventForUser} on the detail screen.
 */
export const getB2BEvents = async (userId, user, navigate) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/user/${userId}`,
      { method: "GET", headers: getAuthJsonHeaders() },
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getB2BEvents(userId, user, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (
      response.status === HTTP_BAD_REQUEST ||
      response.status === HTTP_NOT_FOUND
    ) {
      return [];
    }
    if (Array.isArray(data)) {
      const currentDate = new Date();
      const availableEvents = filterBusinessStyleEventsForUserList(
        data,
        user,
        currentDate,
      );
      return availableEvents.sort(
        (a, b) => new Date(a.fechaInicioEvento) - new Date(b.fechaInicioEvento),
      );
    }
    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * List B2B events visible to the user (lightweight).
 * Backend applies filtering (activo, fechaFinEvento, fechaDisponibleSocios/NoSocios, user type).
 * Does not include userAppointments, disponibilidadEjecutivosRegistro, or duration hints — use {@link getB2BEventForUser} on the detail screen.
 */
export const getB2BEventsForManagement = async (
  userId,
  user,
  navigate,
  permissions,
) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/user/${userId}/management`,
      { method: "GET", headers: getAuthJsonHeaders() },
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getB2BEventsForManagement(userId, user, navigate, permissions),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (
      response.status === HTTP_BAD_REQUEST ||
      response.status === HTTP_NOT_FOUND
    ) {
      return [];
    }
    if (Array.isArray(data)) {
      const includePast = canViewPastEvents(user, permissions);
      const currentDate = new Date();
      const availableEvents = filterNonCancelledEvents(
        data.filter((event) =>
          isB2BManagementEventVisible(event, currentDate, { includePast }),
        ),
      );
      return sortEventsFuturesFirst(availableEvents, currentDate);
    }
    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * List active B2B events (for Events page / Asistencia / Reportes).
 * @param {Function} navigate
 * @param {{ includePast?: boolean }} [options]
 */
export const getActiveB2BEvents = async (navigate, options = {}) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/active`,
      { method: "GET", headers: getAuthJsonHeaders() },
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getActiveB2BEvents(navigate, options),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (Array.isArray(data)) {
      const includePast = Boolean(options.includePast);
      const currentDate = new Date();
      return filterNonCancelledEvents(
        data.filter((event) =>
          isActiveB2BEventVisible(event, currentDate, { includePast }),
        ),
      );
    }
    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Fetches all B2B events for a given year.
 * @async
 * @param {string} year - Year to filter events by
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Array of B2B events for the year
 */
export const getB2BEventsByYear = async (year, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/year/${year}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getB2BEventsByYear(year, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (Array.isArray(data)) {
      return data;
    }
    return [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Get all B2B events for name validation (no date filter).
 */
export const getAllB2BEvents = async (navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/all`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getAllB2BEvents(navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getData = async (userId, changePermissions, navigate, setUser) => {
  try {
    const permissions = await getUserRoleData(
      userId,
      changePermissions,
      navigate,
    );
    const userData = await getUser(userId);
    setUser(userData);
    return await getB2BEventsForManagement(
      userId,
      userData,
      navigate,
      permissions,
    );
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Enriched B2B event for the current user: appointments, executive register availability, slot duration.
 * GET /b2bEvent/{eventId}/user/{userId}
 *
 * @param {string} eventId - B2B event _id
 * @param {string} userId - Authenticated user id
 * @param {Function} navigate - React Router navigate
 * @returns {Promise<object|null>} Event payload or null if not found / error
 */
export const getB2BEventForUser = async (eventId, userId, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/${eventId}/user/${userId}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getB2BEventForUser(eventId, userId, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return null;
      }
      return tokenResult.result;
    }
    if (
      response.status === HTTP_BAD_REQUEST ||
      response.status === HTTP_NOT_FOUND ||
      data?.message === B2B_EVENT_NOT_FOUND_MESSAGE ||
      data?.message === "User not found"
    ) {
      return null;
    }
    if (!response.ok) {
      return null;
    }
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * Get one B2B event by ID (admin/raw event; no per-user enrichment).
 */
export const getB2BEvent = async (eventId, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/${eventId}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getB2BEvent(eventId, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return null;
      }
      return tokenResult.result;
    }
    if (data?.message === B2B_EVENT_NOT_FOUND_MESSAGE) {
      return null;
    }
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * Get one B2B event and format for form.
 */
export const getB2BEventDetails = async (
  eventId,
  navigate,
  dayjs,
  setFormData,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/${eventId}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await getB2BEventDetails(eventId, navigate, dayjs, setFormData);
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }
    if (data?.message === B2B_EVENT_NOT_FOUND_MESSAGE) {
      return;
    }
    setFormData({
      ...data,
      audiencia: normalizeAudienciaFromApi(data.audiencia),
      fechaInicioEvento: data.fechaInicioEvento
        ? dayjs(data.fechaInicioEvento).utc().format(DATE_FORMAT_YYYY_MM_DD)
        : null,
      fechaFinEvento: data.fechaFinEvento
        ? dayjs(data.fechaFinEvento).utc().format(DATE_FORMAT_YYYY_MM_DD)
        : null,
      fechaDisponibleSocios: data.fechaDisponibleSocios
        ? dayjs(data.fechaDisponibleSocios).utc().format(DATE_FORMAT_YYYY_MM_DD)
        : null,
      fechaDisponibleNoSocios: data.fechaDisponibleNoSocios
        ? dayjs(data.fechaDisponibleNoSocios)
            .utc()
            .format(DATE_FORMAT_YYYY_MM_DD)
        : null,
      fechaVigenciaCompradores: data.fechaVigenciaCompradores
        ? dayjs(data.fechaVigenciaCompradores)
            .utc()
            .format(DATE_FORMAT_YYYY_MM_DD)
        : null,
      fechaLimiteRegistro: data.fechaLimiteRegistro
        ? dayjs(data.fechaLimiteRegistro).utc().format(DATE_FORMAT_YYYY_MM_DD)
        : null,
      horaInicio: normalizeTimeForInput(data.horaInicio),
      horaFin: normalizeTimeForInput(data.horaFin),
    });
  } catch (error) {
    console.error(error);
  }
};

/**
 * List participating companies for a B2B event (generic event API).
 */
export const getParticipatingCompanies = async (
  eventId,
  navigate,
  setParticipatingCompanies,
) => {
  const userId = localStorage.getItem("userId");
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}participatingCompany/event/${eventId}/participatingCompanies/${userId}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await getParticipatingCompanies(
        eventId,
        navigate,
        setParticipatingCompanies,
      );
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }
    setParticipatingCompanies(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error(error);
    setParticipatingCompanies([]);
  }
};

/**
 * Participating buyer companies with full buyer executives.
 * GET /b2bEvent/{eventId}/participatingCompanies
 *
 * @param {string} eventId - B2B event _id
 * @param {import("react-router").NavigateFunction} navigate
 * @returns {Promise<Array>}
 */
export const getB2BEventParticipatingCompanies = async (eventId, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/${eventId}/participatingCompanies`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getB2BEventParticipatingCompanies(eventId, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (!response.ok) {
      return [];
    }
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Virtual line: active B2B appointments for one buyer executive on an event.
 * GET /b2bEvent/{eventId}/buyer/{buyerUserId}/appointments
 *
 * @param {string} eventId - B2B event _id
 * @param {string} buyerUserId - Buyer executive user _id
 * @param {import("react-router").NavigateFunction} navigate
 * @param {Record<string, string>|null} [virtualLineQuery] - Optional slot filter from `ejecutivosCompradores` schedule row: `fecha`, `horaInicio`, `horaFin` (buyer availability window, not individual cita times).
 * @returns {Promise<Array>}
 */
export const getB2BEventBuyerAppointmentsVirtualLine = async (
  eventId,
  buyerUserId,
  navigate,
  virtualLineQuery = null,
) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  const qs =
    virtualLineQuery &&
    typeof virtualLineQuery === "object" &&
    Object.keys(virtualLineQuery).length > 0
      ? `?${new URLSearchParams(virtualLineQuery).toString()}`
      : "";

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/${eventId}/buyer/${buyerUserId}/appointments${qs}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getB2BEventBuyerAppointmentsVirtualLine(
        eventId,
        buyerUserId,
        navigate,
        virtualLineQuery,
      ),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return [];
      }
      return tokenResult.result;
    }
    if (!response.ok) {
      return [];
    }
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

/**
 * Executive buyer: virtual line(s) for the logged-in user across B2B events where they are in ejecutivosCompradores.
 * GET /b2bEvent/user/{userId}/buyerVirtualLine
 *
 * @param {string} userId - App user _id (logged-in user)
 * @param {import("react-router").NavigateFunction} navigate
 * @returns {Promise<{ events: Array<{ eventId: string, event: object, appointments: Array }> }>}
 */
export const getB2BEventBuyerVirtualLineForUser = async (userId, navigate) => {
  const options = {
    method: "GET",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/user/${userId}/buyerVirtualLine`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      getB2BEventBuyerVirtualLineForUser(userId, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return { events: [] };
      }
      return tokenResult.result;
    }
    if (response.status === HTTP_NOT_FOUND) {
      return { events: [] };
    }
    if (!response.ok) {
      return { events: [] };
    }
    const events = Array.isArray(data?.events) ? data.events : [];
    return { events };
  } catch (error) {
    console.error(error);
    return { events: [] };
  }
};

/**
 * Apply virtual-line pause shift: moves all appointments after the anchor by a signed duration.
 * POST /b2bEvent/{eventId}/buyer/{buyerUserId}/appointments/pause-shift
 *
 * @param {string} eventId - B2B event _id
 * @param {string} buyerUserId - Buyer executive _id (same as GET virtual line)
 * @param {string} appointmentId - Anchor appointment _id (row where user tapped Pause)
 * @param {string} pauseDuration - Signed `H:mm` / `HH:mm`, e.g. `0:10`, `-1:00`
 * @param {import("react-router").NavigateFunction} navigate
 * @returns {Promise<{ data?: { message?: string, updatedCount?: number }, error?: string }>}
 */
export const pauseShiftB2BEventBuyerAppointments = async (
  eventId,
  buyerUserId,
  appointmentId,
  pauseDuration,
  navigate,
) => {
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({ appointmentId, pauseDuration }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}b2bEvent/${eventId}/buyer/${buyerUserId}/appointments/pause-shift`,
      options,
    );

    let data = {};
    try {
      const text = await response.text();
      if (text) {
        data = JSON.parse(text);
      }
    } catch {
      data = {};
    }

    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      pauseShiftB2BEventBuyerAppointments(
        eventId,
        buyerUserId,
        appointmentId,
        pauseDuration,
        navigate,
      ),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return { error: "auth" };
      }
      return tokenResult.result;
    }

    if (response.ok) {
      return { data };
    }

    return {
      error:
        typeof data?.message === "string"
          ? data.message
          : "pauseShiftB2BEventBuyerAppointments failed",
    };
  } catch (error) {
    logDevError(error);
    return {
      error: apiMessageOrGeneric(error.message, "NETWORK_ERROR"),
    };
  }
};

/**
 * Register for a B2B event (creates appointment with first available slot).
 * @param {string} idEventoB2B - B2B event ID
 * @param {string} idEmpresaCompradora - Buyer company ID
 * @param {string} idUsuarioComprador - Buyer user ID (ejecutivo comprador _id)
 * @param {Function} navigate - React Router navigate
 * @param {string} [idEmpresaUsuarioRemitente] - Sender company ID (optional, from currentUser.idEmpresa)
 * @returns {Promise<{ data?: object, error?: string }>}
 */
/**
 * Registration price (MXN) for the current user from B2B event cost fields.
 * Mirrors business-event logic: `tipoDeUsuario` `"4"` uses non-member price; `"1"`,`"2"`,`"3"`,`"5"` use member price.
 *
 * @param {Record<string, unknown>|null|undefined} event - B2B event document
 * @param {Record<string, unknown>|null|undefined} user - User with `tipoDeUsuario`
 * @returns {number} Non-negative amount; `0` means no charge / skip payment UI
 */
export const getB2BEventPriceForUser = (event, user) => {
  if (!event || !user) {
    return 0;
  }
  const raw =
    user.tipoDeUsuario === "4" ? event.costoNoSocios : event.costoSocios;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Marks a B2B event appointment as paid (after successful card charge, etc.).
 * Backend: `PATCH /attendee/b2bEvent/appointment/payment`.
 *
 * @param {import("react-router").NavigateFunction} navigate
 * @param {string} idEventoB2B - B2B event id
 * @param {string} idCita - Appointment `_id`
 * @param {string} userId - Sender `_id` (`idUsuarioRemitente`); sent as **`idUsuario`** (required) and **`pagadoPor`** per API-DOCS.
 * @returns {Promise<Record<string, unknown>|null>} Updated appointment payload or `null` on failure
 */
export const markB2BEventAppointmentPaid = async (
  navigate,
  idEventoB2B,
  idCita,
  userId,
) => {
  const options = {
    method: "PATCH",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({
      idUsuario: userId,
      idEventoB2B,
      idCita,
      appointmentInformation: {
        pagado: true,
        pagadoPor: userId,
      },
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/b2bEvent/appointment/payment`,
      options,
    );
    const data = await response.json();

    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      markB2BEventAppointmentPaid(navigate, idEventoB2B, idCita, userId),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return null;
      }
      return tokenResult.result;
    }

    if (response.ok && data?.data != null) {
      return data.data;
    }
    console.error(
      "markB2BEventAppointmentPaid failed:",
      data?.message || response.status,
    );
    return null;
  } catch (error) {
    console.error("markB2BEventAppointmentPaid error:", error);
    return null;
  }
};

export const registerB2BEvent = async (
  idEventoB2B,
  idEmpresaCompradora,
  idUsuarioComprador,
  navigate,
  idEmpresaUsuarioRemitente,
) => {
  const idUsuarioRemitente = localStorage.getItem("userId");
  const senderCompanyId =
    idEmpresaUsuarioRemitente || localStorage.getItem("companyId") || "";
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({
      idEventoB2B,
      idEmpresaCompradora,
      idUsuarioComprador,
      idUsuarioRemitente,
      idEmpresaUsuarioRemitente: senderCompanyId,
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/b2bEvent`,
      options,
    );
    const data = await response.json();

    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      registerB2BEvent(
        idEventoB2B,
        idEmpresaCompradora,
        idUsuarioComprador,
        navigate,
        idEmpresaUsuarioRemitente,
      ),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return { error: "auth" };
      }
      return tokenResult.result;
    }

    if (response.ok && data?.data) {
      return { data: data.data };
    }
    return {
      error: apiMessageOrGeneric(data?.message, "FAILED_TO_REGISTER"),
      code: data?.code,
    };
  } catch (error) {
    logDevError("Error registering for B2B event:", error);
    return {
      error: apiMessageOrGeneric(error.message, "NETWORK_ERROR"),
    };
  }
};

/**
 * Cancel a B2B event appointment.
 * @param {string} idUsuario - User ID (comprador or remitente of the appointment)
 * @param {string} idEventoB2B - B2B event ID
 * @param {string} idCita - Appointment ID
 * @param {Function} navigate - React Router navigate
 * @returns {Promise<{ data?: object, error?: string }>}
 */
export const cancelB2BEventAppointment = async (
  idUsuario,
  idEventoB2B,
  idCita,
  navigate,
) => {
  const options = {
    method: "PUT",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({
      idUsuario,
      idEventoB2B,
      idCita,
    }),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/b2bEvent/attendance/cancel`,
      options,
    );
    const data = await response.json();

    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      cancelB2BEventAppointment(idUsuario, idEventoB2B, idCita, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return { error: "auth" };
      }
      return tokenResult.result;
    }

    if (response.ok && data?.data) {
      return { data: data.data };
    }
    return { error: apiMessageOrGeneric(data?.message, "FAILED_TO_CANCEL") };
  } catch (error) {
    logDevError("Error cancelling B2B appointment:", error);
    return {
      error: apiMessageOrGeneric(error.message, "NETWORK_ERROR"),
    };
  }
};

const buildB2BEventSubmitBody = (eventData) => ({
  userId: eventData.userId,
  nombre: eventData.nombre,
  descripcion: eventData.descripcion,
  fechaInicioEvento: eventData.fechaInicioEvento,
  fechaFinEvento: eventData.fechaFinEvento,
  imagenBanner: eventData.imagenBanner || null,
  audiencia: normalizeAudienciaFromApi(eventData.audiencia),
  fechaDisponibleSocios: eventData.fechaDisponibleSocios || null,
  fechaDisponibleNoSocios: eventData.fechaDisponibleNoSocios || null,
  fechaVigenciaCompradores: eventData.fechaVigenciaCompradores || null,
  fechaLimiteRegistro: eventData.fechaLimiteRegistro || null,
  horaInicio: eventData.horaInicio || null,
  horaFin: eventData.horaFin || null,
  costoSocios: eventData.costoSocios || null,
  costoNoSocios: eventData.costoNoSocios || null,
  duracionCitasSocios: eventData.duracionCitasSocios ?? null,
  duracionCitasNoSocios: eventData.duracionCitasNoSocios ?? null,
  ubicacion: eventData.ubicacion || null,
  activo: eventData.activo ?? true,
  nombreImagen: eventData.nombreImagen || undefined,
});

/**
 * Create or update B2B event.
 * @returns {Promise<{ ok: boolean, wasCreate?: boolean, event?: Record<string, unknown> }|undefined>}
 *   `{ ok: true, wasCreate, event }` on success, `{ ok: false }` on failure, or `undefined` if redirected after token expiry.
 */
export const submitB2BEvent = async (
  eventData,
  eventId,
  navigate,
  setSaving,
  setEventData,
  setHasChanges,
) => {
  setSaving(true);
  const wasCreate = !eventId;
  const { Buffer } = await import("buffer");
  try {
    const url = eventId
      ? `${process.env.REACT_APP_ROOT_API}b2bEvent/${eventId}`
      : `${process.env.REACT_APP_ROOT_API}b2bEvent`;
    const method = eventId ? "PUT" : "POST";
    const response = await fetch(url, {
      method,
      headers: getAuthJsonHeaders(),
      body: JSON.stringify(buildB2BEventSubmitBody(eventData)),
    });

    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      submitB2BEvent(
        eventData,
        eventId,
        navigate,
        setSaving,
        setEventData,
        setHasChanges,
      ),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return { ok: false };
      }
      return tokenResult.result;
    }

    if (response.ok && data.event) {
      if (data.presignedUrl && eventData.imagenBanner) {
        await uploadEventBannerFromDataUrl(
          data.presignedUrl,
          eventData.imagenBanner,
          eventData.formatoImagen,
          Buffer,
        );
      }
      setEventData(data.event);
      setHasChanges(false);
      return { ok: true, wasCreate, event: data.event };
    }
    return { ok: false };
  } catch (error) {
    console.error("Error submitting B2B event:", error);
    return { ok: false };
  } finally {
    setSaving(false);
  }
};

/**
 * Initialize B2B event form (edit or create).
 */
export const initializeB2BEventCreation = async ({
  eventId,
  userId,
  changePermissions,
  navigate,
  dayjs,
  setFormData,
  setParticipatingCompanies,
  setIsLoading,
}) => {
  try {
    setIsLoading(true);
    await getUserRoleData(userId, changePermissions, navigate);
    if (eventId) {
      await getB2BEventDetails(eventId, navigate, dayjs, setFormData);
      await getParticipatingCompanies(
        eventId,
        navigate,
        setParticipatingCompanies,
      );
    }
  } catch (error) {
    console.error("Error initializing B2B event creation:", error);
  } finally {
    setIsLoading(false);
  }
};

/**
 * Add participating company (generic event API; idEvento = B2B event id).
 * B2B: ejecutivosCompradores is array of { _id, fecha, horaInicio, horaFin, horaInicioComida, horaFinComida }.
 */
export const addParticipatingCompanyToEvent = async (
  newCompany,
  setParticipatingCompanies,
  setParticipantError,
  navigate,
) => {
  setParticipantError(null);
  const options = {
    method: "POST",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify(newCompany),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}participatingCompany`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await addParticipatingCompanyToEvent(
        newCompany,
        setParticipatingCompanies,
        setParticipantError,
        navigate,
      );
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }
    if (!response.ok) {
      setParticipantError(data?.message || "CREATE_ERROR");
      return;
    }
    setParticipatingCompanies((prev) => [
      ...prev,
      { ...data, puedeEliminarse: true },
    ]);
  } catch (error) {
    console.error(error);
    setParticipantError("CREATE_ERROR");
  }
};

/**
 * Update participating company (generic event API).
 */
export const updateParticipatingCompany = async (
  updatedCompany,
  setParticipatingCompanies,
  setParticipantError,
  navigate,
) => {
  setParticipantError(null);
  const options = {
    method: "PUT",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify(updatedCompany),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}participatingCompany`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, async () => {
      await updateParticipatingCompany(
        updatedCompany,
        setParticipatingCompanies,
        setParticipantError,
        navigate,
      );
    });
    if (tokenResult?.redirected) {
      return;
    }
    if (tokenResult) {
      return;
    }
    if (!response.ok) {
      setParticipantError(data?.message || "UPDATE_ERROR");
      return;
    }
    setParticipatingCompanies((prev) =>
      prev.map((pc) =>
        pc.idEmpresaCompradora === data.idEmpresaCompradora ? data : pc,
      ),
    );
  } catch (error) {
    console.error("Error updating participating company:", error);
    setParticipantError("UPDATE_ERROR");
  }
};

/**
 * Delete participating company (generic event API).
 */
export const deleteParticipatingCompany = async (participant, navigate) => {
  const options = {
    method: "DELETE",
    headers: getAuthJsonHeaders(),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}participatingCompany/${participant.idEmpresaCompradora}/event/${participant.idEvento}`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      deleteParticipatingCompany(participant, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return false;
      }
      return tokenResult.result;
    }
    return response.ok;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const reviewB2BEventAppointment = async (
  appointment,
  rating,
  navigate,
) => {
  const idUsuario = localStorage.getItem("userId");
  const idEventoB2B = appointment.idEventoB2B;
  const idCita = appointment._id;
  const options = {
    method: "PUT",
    headers: getAuthJsonHeaders(),
    body: JSON.stringify({
      idUsuario,
      idEventoB2B,
      idCita,
      calificacionCita: rating,
    }),
  };
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}attendee/b2bEvent/attendance/rate`,
      options,
    );
    const data = await response.json();
    const tokenResult = await resolveExpiredToken(data, navigate, () =>
      reviewB2BEventAppointment(appointment, rating, navigate),
    );
    if (tokenResult) {
      if (tokenResult.redirected) {
        return false;
      }
      return tokenResult.result;
    }
    return response.ok;
  } catch (error) {
    console.error(error);
    return false;
  }
};
